import { $, $$, el, pageData } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';

/**
 * The Invitations page.
 *
 * One setting, a ladder of rewards, and a leaderboard above them. The
 * leaderboard is rendered by the server and never posted back: who invited whom
 * is a deduction the bot made at the moment somebody arrived, not a preference
 * anybody holds.
 */
interface Reward {
  invites: number;
  roleId: string;
}

interface Role {
  id: string;
  name: string;
  aboveBot?: boolean;
}

interface Settings {
  countLeavers: boolean;
  rewards: Reward[];
}

export function initInvitesPage(): void {
  const found = $<HTMLFormElement>('[data-invites-form]');
  if (!found) return;

  // Captured into a non-null constant: the narrowing above does not reach the
  // functions declared below, which are hoisted.
  const form: HTMLFormElement = found;

  const { settings, guildId, roles } = pageData<{
    settings: Settings;
    guildId: string;
    roles: Role[];
  }>();

  const rows = $('[data-invite-reward-rows]', form);
  const empty = $('[data-invite-reward-empty]', form);

  trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: async () => {
      await api.put(`/api/invites?guildId=${guildId}`, {
        guildId,
        countLeavers: checkbox(form, 'countLeavers'),
        rewards: readRewards(),
      });
    },
  });

  /** Rebuilds the ladder from the rows currently on screen. */
  function readRewards(): Reward[] {
    return $$<HTMLElement>('[data-invite-reward-row]', form)
      .map((row) => ({
        invites: Number(row.querySelector<HTMLInputElement>('[data-reward-invites]')?.value ?? 0),
        roleId: row.querySelector<HTMLSelectElement>('[data-reward-role]')?.value ?? '',
      }))
      .filter((reward) => reward.invites > 0 && reward.roleId !== '');
  }

  function renderEmptyState(): void {
    empty?.classList.toggle('hidden', $$('[data-invite-reward-row]', form).length > 0);
  }

  /** One rung: a number of invitations, a role, and a way to remove it. */
  function addRow(reward: Reward = { invites: 5, roleId: '' }): void {
    if (!rows) return;

    const row = el('div', {
      class: 'flex flex-wrap items-end gap-2',
      attrs: { 'data-invite-reward-row': '' },
    });

    const count = el('input', {
      class: 'input input-bordered w-24',
      attrs: {
        type: 'number',
        min: '1',
        max: '10000',
        'data-reward-invites': '',
        'aria-label': ct('rewardInvites', 'Invitations'),
      },
    });
    count.value = String(reward.invites);

    const rolePicker = el('select', {
      class: 'select select-bordered min-w-48 flex-1',
      attrs: { 'data-reward-role': '', 'aria-label': ct('rewardRole', 'Role') },
    });
    rolePicker.append(
      el('option', { text: ct('chooseRole', '- Pick a role -'), attrs: { value: '' } }),
    );

    for (const role of roles) {
      const option = el('option', {
        text: role.aboveBot
          ? `@${role.name} · ${ct('outOfReach', 'out of reach')}`
          : `@${role.name}`,
        attrs: { value: role.id },
      });
      // A role above the bot cannot be granted: offering it would promise a
      // reward the bot is unable to hand out.
      if (role.aboveBot) option.disabled = true;
      rolePicker.append(option);
    }
    rolePicker.value = reward.roleId;

    const remove = el('button', {
      class: 'btn btn-ghost btn-sm',
      text: '✕',
      attrs: {
        type: 'button',
        'aria-label': ct('removeItem', 'Remove {name}', { name: '' }).trim(),
      },
    });
    remove.addEventListener('click', () => {
      row.remove();
      renderEmptyState();
      form.dispatchEvent(new Event('change', { bubbles: true }));
    });

    row.append(count, rolePicker, remove);
    rows.append(row);
    renderEmptyState();
  }

  for (const reward of settings.rewards) addRow(reward);
  renderEmptyState();

  $('[data-invite-reward-add]', form)?.addEventListener('click', () => {
    addRow();
    form.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const field = form.elements.namedItem(name);
  return field instanceof HTMLInputElement ? field.checked : false;
}
